import React, { useRef } from 'react';
import { useAct, gsap, prefersReduced } from '../hooks/useScrollStory';

/* ══════════════════════════════════════════════════════
   SORUNDAN TAKİBE, TEK YERDE
   GÖR → ANLA → TAKİP ET. Üç kart, gerçek uygulama ekranları.
   Kartlar tıklanabilir; üstüne gelince imlece göre 3B dönüyor
   (kutu gibi eğiliyor) ve hafifçe kalkıyor.
   ══════════════════════════════════════════════════════ */

const CARDS = [
  {
    step: 'Gör', href: '#uydudan', img: '/assets/gallery/lovic1.webp',
    title: 'Bitkinin ve tarlanın durumunu incele',
    desc: 'Uydu ve analiz katmanlarıyla tarlanı harita üzerinden gör; dikkat çeken bölgeleri fark et.',
  },
  {
    step: 'Anla', href: '#ozellikler', img: '/assets/gallery/lovic3.webp',
    title: 'Verileri ve AI analizini yorumla',
    desc: 'Fotoğraf analizini ve tarla verilerini birlikte değerlendir; olası nedenleri daha iyi anla.',
  },
  {
    step: 'Takip et', href: '#ozellikler', img: '/assets/gallery/lovic2.webp',
    title: 'Değişimleri ve günlük işlerini düzenle',
    desc: 'Tarla bilgilerini, görevlerini ve üretim sürecindeki önemli noktaları tek yerde tut.',
  },
];

function TiltCard({ card, i }) {
  const ref = useRef(null);
  const glareRef = useRef(null);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(1000px) rotateY(${px * 15}deg) rotateX(${-py * 15}deg) translateY(-8px)`;
    if (glareRef.current) {
      glareRef.current.style.opacity = '1';
      glareRef.current.style.background =
        `radial-gradient(circle at ${(px + 0.5) * 100}% ${(py + 0.5) * 100}%, rgba(200,255,34,.16), rgba(200,255,34,0) 55%)`;
    }
  };
  const onLeave = () => {
    const el = ref.current;
    if (el) el.style.transform = 'perspective(1000px) rotateY(0) rotateX(0) translateY(0)';
    if (glareRef.current) glareRef.current.style.opacity = '0';
  };

  return (
    <a
      href={card.href}
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="st-card group relative block rounded-[24px] border border-white/10 overflow-hidden no-underline"
      style={{
        background: 'linear-gradient(160deg, rgba(255,255,255,.05) 0%, rgba(255,255,255,.015) 42%, rgba(255,255,255,0) 100%)',
        transformStyle: 'preserve-3d',
        transition: 'transform .18s ease-out, border-color .3s ease',
        willChange: 'transform',
      }}
    >
      {/* üst hairline */}
      <div className="absolute top-0 left-0 right-0 h-px z-20" aria-hidden="true"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(200,255,34,.6), transparent)' }} />

      {/* görsel */}
      <div className="relative overflow-hidden" style={{ aspectRatio: '4 / 3' }}>
        <img src={card.img} alt={card.title} loading="lazy" decoding="async"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.06]" />
        <div className="absolute inset-0" aria-hidden="true"
          style={{ background: 'linear-gradient(180deg, rgba(5,6,5,0) 40%, rgba(5,6,5,.72) 100%)' }} />
        {/* adım rozeti */}
        <div className="absolute left-5 bottom-4 flex items-center gap-2">
          <span className="font-mono text-[11px]" style={{ color: '#C8FF22' }}>0{i + 1}</span>
          <span className="font-display font-bold text-white" style={{ fontSize: '22px', textShadow: '0 2px 14px rgba(0,0,0,.6)' }}>
            {card.step}
          </span>
        </div>
      </div>

      {/* metin */}
      <div className="relative p-6 md:p-7">
        <h3 className="font-display font-semibold text-white leading-[1.2]" style={{ fontSize: 'clamp(17px,1.5vw,20px)' }}>
          {card.title}
        </h3>
        <p className="mt-3 text-[14px] leading-relaxed text-white/55">{card.desc}</p>
        <span className="mt-5 inline-flex items-center gap-1.5 text-[13.5px] font-medium" style={{ color: '#C8FF22' }}>
          İncele <span className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true">→</span>
        </span>
      </div>

      {/* imleci izleyen parıltı */}
      <div ref={glareRef} className="absolute inset-0 pointer-events-none opacity-0 transition-opacity duration-300" aria-hidden="true" />
    </a>
  );
}

export default function StackedPanels() {
  const ref = useAct((root) => {
    const q = gsap.utils.selector(root);
    if (prefersReduced()) return;
    gsap.from(q('.st-reveal'), {
      opacity: 0, y: 36, ease: 'power3.out', duration: 0.7, stagger: 0.12,
      scrollTrigger: { trigger: root, start: 'top 74%' },
    });
  }, []);

  return (
    <section ref={ref} id="sistem" className="relative overflow-hidden" style={{ background: '#050605' }}>
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true"
        style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 12%, rgba(200,255,34,.08) 0%, rgba(200,255,34,0) 60%)' }} />

      <div className="relative max-w-[1120px] mx-auto px-6 md:px-10 py-[clamp(84px,14vh,150px)]">
        <div className="st-reveal text-center max-w-[720px] mx-auto">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.03] px-4 py-1.5 font-mono text-[11.5px] tracking-[.2em] uppercase"
            style={{ color: '#C8FF22' }}>
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: '#C8FF22', boxShadow: '0 0 10px #C8FF22' }} />
            Gör → Anla → Takip et
          </span>
          <h2 className="mt-6 font-display font-bold text-white leading-[1.05] tracking-[-0.03em]"
            style={{ fontSize: 'clamp(28px,3.8vw,50px)' }}>
            Sorundan takibe, tek yerde
          </h2>
          <p className="mt-5 text-[16px] leading-relaxed text-white/60">
            Agren AI; bitki analizi, tarla verileri ve yapay zekâ asistanını aynı deneyimde bir araya getirir.
          </p>
        </div>

        <div className="mt-[clamp(44px,6vh,72px)] grid grid-cols-1 md:grid-cols-3 gap-6" style={{ perspective: '1200px' }}>
          {CARDS.map((c, i) => (
            <div key={c.step} className="st-reveal">
              <TiltCard card={c} i={i} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
